import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageParts";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Save, Lock, RotateCcw } from "lucide-react";
import { useAuth } from "@/lib/auth";

const ROLE_LABELS = { superadmin: "Superadmin", admin: "Admin", account: "Account" };
const ROLE_ORDER = ["superadmin", "admin", "account"];

export default function RolesPermissions() {
  const { user, refreshPermissions } = useAuth();
  const [capabilities, setCapabilities] = useState([]);
  const [matrix, setMatrix] = useState({ superadmin: {}, admin: {}, account: {} });
  const [dirty, setDirty] = useState({ admin: false, account: false });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/permissions");
      setCapabilities(data.capabilities);
      setMatrix(data.matrix);
      setDirty({ admin: false, account: false });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load permissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sections = useMemo(() => {
    const grouped = {};
    for (const cap of capabilities) {
      if (!grouped[cap.section]) grouped[cap.section] = [];
      grouped[cap.section].push(cap);
    }
    return grouped;
  }, [capabilities]);

  const toggle = (role, key, value) => {
    if (role === "superadmin") return; // locked
    setMatrix((prev) => ({ ...prev, [role]: { ...prev[role], [key]: value } }));
    setDirty((prev) => ({ ...prev, [role]: true }));
  };

  const save = async (role) => {
    setSaving(true);
    try {
      await api.put("/settings/permissions", { role, permissions: matrix[role] });
      toast.success(`${ROLE_LABELS[role]} permissions saved. Changes apply on next page load for users.`);
      setDirty((prev) => ({ ...prev, [role]: false }));
      if (user?.role === role) await refreshPermissions?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const saveAll = async () => {
    if (dirty.admin) await save("admin");
    if (dirty.account) await save("account");
  };

  if (loading) {
    return (
      <PageBody>
        <div className="text-sm text-slate-500 py-12 text-center">Loading permissions…</div>
      </PageBody>
    );
  }

  return (
    <>
      <PageHeader
        title="Roles & Permissions"
        subtitle="Toggle capabilities per role. Superadmin has full access and cannot be edited. Changes apply on next page load."
      >
        <Button variant="outline" size="sm" onClick={load} data-testid="permissions-reload">
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reload
        </Button>
        <Button
          size="sm"
          onClick={saveAll}
          disabled={saving || (!dirty.admin && !dirty.account)}
          data-testid="permissions-save-all"
        >
          <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save all changes"}
        </Button>
      </PageHeader>
      <PageBody className="space-y-6">
        <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="w-[40%]">Capability</TableHead>
                {ROLE_ORDER.map((role) => (
                  <TableHead key={role} className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <span>{ROLE_LABELS[role]}</span>
                      {role === "superadmin" && <Lock className="w-3 h-3 text-slate-400" />}
                      {dirty[role] && <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-700">unsaved</Badge>}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(sections).map(([section, caps]) => (
                <React.Fragment key={section}>
                  <TableRow className="bg-slate-100 hover:bg-slate-100">
                    <TableCell colSpan={4} className="font-heading font-semibold text-xs uppercase tracking-wider text-slate-600 py-2">
                      {section}
                    </TableCell>
                  </TableRow>
                  {caps.map((cap) => (
                    <TableRow key={cap.key} data-testid={`perm-row-${cap.key}`}>
                      <TableCell className="text-sm text-slate-800">
                        <div>{cap.label}</div>
                        <div className="text-[10px] font-mono text-slate-400">{cap.key}</div>
                      </TableCell>
                      {ROLE_ORDER.map((role) => {
                        const value = !!matrix[role]?.[cap.key];
                        const locked = role === "superadmin";
                        return (
                          <TableCell key={role} className="text-center">
                            <Switch
                              checked={value}
                              disabled={locked}
                              onCheckedChange={(v) => toggle(role, cap.key, v)}
                              data-testid={`perm-toggle-${role}-${cap.key}`}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => save("admin")} disabled={!dirty.admin || saving} data-testid="permissions-save-admin">
            Save Admin
          </Button>
          <Button variant="outline" onClick={() => save("account")} disabled={!dirty.account || saving} data-testid="permissions-save-account">
            Save Account
          </Button>
          <Button onClick={saveAll} disabled={saving || (!dirty.admin && !dirty.account)} data-testid="permissions-save-all-bottom">
            <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save all changes"}
          </Button>
        </div>
      </PageBody>
    </>
  );
}
